import { useRef, useState, useEffect } from 'react';
import { Mail, StickyNote, Users, UserPlus, User, CalendarClock } from 'lucide-react';
import { getIconoPago } from '@/components/constants/citas';
import ReprogramacionTooltip from '@/components/pages/shared/ReprogramacionTooltip';

interface EventCardProps {
  event: any;
  cfg: any;
  t: (key: string, opts?: any) => string;
  idEmpresa: string;
  agregarCliente: (
    idEmpresa: string,
    email: string,
    event: any,
    cb: () => void
  ) => void;
  onReload: () => void;
  calendarView: string;
  onWhatsappClick: () => void;
}

const WhatsappIcon = ({ className }: { className: string }) => (
  <svg
    viewBox="0 0 24 24"
    className={`${className} fill-green-500`}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.554 4.118 1.525 5.847L.057 23.986l6.305-1.654A11.954 11.954 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.882a9.88 9.88 0 01-5.034-1.376l-.361-.214-3.741.981.999-3.648-.235-.374A9.859 9.859 0 012.118 12C2.118 6.534 6.534 2.118 12 2.118c5.465 0 9.882 4.416 9.882 9.882 0 5.465-4.417 9.882-9.882 9.882z" />
  </svg>
);

// Formatea 'YYYY-MM-DD' a algo corto como '24 jun'
const formatFechaCorta = (fecha: string) =>
  new Date(`${fecha}T00:00:00`).toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
  });

const formatHoraCorta = (hora: string) => (hora ? hora.slice(0, 5) : '');

export default function EventCard({
  event,
  cfg,
  t,
  idEmpresa,
  agregarCliente,
  onReload,
  calendarView,
  onWhatsappClick,
}: EventCardProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [availableH, setAvailableH] = useState(0);
  const [availableW, setAvailableW] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const el = containerRef.current?.closest('.fc-event') as HTMLElement | null;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setAvailableH(el.offsetHeight);
      setAvailableW(el.offsetWidth);
    });
    ro.observe(el);
    setAvailableH(el.offsetHeight);
    setAvailableW(el.offsetWidth);
    return () => ro.disconnect();
  }, []);

  if (!event || !event.extendedProps) return null;

  const est = event.extendedProps.estado?.toLowerCase();
  const fase = event.extendedProps.tipo_fase;

  let borderColor = 'border-blue-500';
  let bgColor = 'bg-blue-100 dark:bg-blue-900';
  let textMuted = 'text-blue-700 dark:text-blue-300';

  if (est === 'cancelado') {
    borderColor = 'border-red-400';
    bgColor = 'bg-red-100 dark:bg-red-900';
    textMuted = 'text-red-600 dark:text-red-400';
  } else if (est === 'completado') {
    borderColor = 'border-green-500';
    bgColor = 'bg-green-100 dark:bg-green-900';
    textMuted = 'text-green-700 dark:text-green-400';
  } else if (est === 'anulado') {
    borderColor = 'border-yellow-400';
    bgColor = 'bg-yellow-100 dark:bg-yellow-900';
    textMuted = 'text-yellow-600 dark:text-yellow-400';
  } else if (fase === 'descanso') {
    borderColor = 'border-gray-300';
    bgColor = 'bg-gray-200 dark:bg-gray-700';
    textMuted = 'text-gray-500 dark:text-gray-400';
  }

  const hi = event.start?.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
  const hf = event.end?.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
  const icon = getIconoPago(event.extendedProps.tipo_pago);

  const nombreCliente = event.extendedProps.nombre_cliente || '';
  const nombreServicio =
    event.extendedProps.nombre_servicio || event.title || '';
  const nombreTrabajador = event.extendedProps.trabajadores_en_cita?.length
    ? event.extendedProps.trabajadores_en_cita
        .map((tw: any) => tw.nombre)
        .join(', ')
    : '';

  const servicioTrabajador = [
    cfg.mostrar_servicio && nombreServicio ? nombreServicio : '',
    cfg.mostrar_trabajador && nombreTrabajador ? nombreTrabajador : '',
  ]
    .filter(Boolean)
    .join(' · ');

  // ── Última reprogramación (cliente o trabajador) ──────────────
  const historialReprogramaciones: any[] =
    event.extendedProps.historial_reprogramaciones ?? [];
  const ultimaReprogramacion = historialReprogramaciones[0];
  const fechaAnteriorTexto = ultimaReprogramacion
    ? `${formatFechaCorta(ultimaReprogramacion.fecha_anterior)} ${formatHoraCorta(
        ultimaReprogramacion.hora_anterior
      )}`
    : '';

  // Bandas de descanso dentro del bloque: la cita es un unico evento y las
  // fases sin atencion se pintan como franjas, en porcentaje sobre el alto.
  const fasesTramo: any[] = event.extendedProps.fases_tramo ?? [];
  const minutosTotales = fasesTramo.reduce(
    (acc, f) => acc + (Number(f.duracion_minutos) || 0),
    0
  );
  const bandas: {
    key: string;
    tenir: boolean;
    top: number;
    alto: number;
    hora: string;
    nombre: string;
  }[] = [];

  if (calendarView.startsWith('timeGrid') && minutosTotales > 0 && event.start) {
    const inicioMs = event.start.getTime();
    const hhmm = (min: number) =>
      new Date(inicioMs + min * 60000).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });

    let acumulado = 0;

    fasesTramo.forEach((f, i) => {
      const dur = Number(f.duracion_minutos) || 0;
      if (dur <= 0) return;

      const desde = acumulado;
      acumulado += dur;

      // La primera fase no lleva etiqueta: ahi esta la cabecera de la cita.
      // Solo se tiñen los descansos; teñir tambien las de atencion dejaba el
      // bloque a franjas de varios azules.
      if (i === 0) return;

      bandas.push({
        key: `${f.id_fase ?? i}`,
        tenir: !f.requiere_atencion,
        top: (desde / minutosTotales) * 100,
        alto: (dur / minutosTotales) * 100,
        hora: `${hhmm(desde)} – ${hhmm(acumulado)}`,
        nombre: f.nombre_fase ?? '',
      });
    });
  }

  const timeText = isMobile ? 'text-xs' : 'text-[10px]';
  const infoText = isMobile ? 'text-[11px]' : 'text-[10px]';

  const ROW_H = isMobile ? 16 : 13;
  const HORA_H = isMobile ? 16 : 13;

  const infoLines: { key: string; icon?: React.ReactNode; text: string }[] = [];

  if (fechaAnteriorTexto)
    infoLines.push({
      key: 'fecha-anterior',
      text: fechaAnteriorTexto,
    });

  if (servicioTrabajador)
    infoLines.push({ key: 'servicio-trabajador', text: servicioTrabajador });

  if (nombreCliente || (cfg.mostrar_telefono && event.extendedProps.telefono))
    infoLines.push({
      key: 'cliente-telefono',
      icon: <User className="w-2.5 h-2.5 flex-shrink-0" />,
      text: [
        nombreCliente,
        cfg.mostrar_telefono && event.extendedProps.telefono
          ? event.extendedProps.telefono
          : '',
      ]
        .filter(Boolean)
        .join(' · '),
    });

  if (event.extendedProps.num_clientes > 1)
    infoLines.push({
      key: 'num-clientes',
      icon: <Users className="w-2.5 h-2.5 flex-shrink-0" />,
      text: `${event.extendedProps.num_clientes} personas`,
    });

  if (cfg.mostrar_email && event.extendedProps.email_contacto)
    infoLines.push({
      key: 'email',
      icon: <Mail className="w-2.5 h-2.5 flex-shrink-0" />,
      text: event.extendedProps.email_contacto,
    });

  const showTipoPago =
    cfg.mostrar_tipo_pago &&
    (est === 'completado' || est === 'anulado') &&
    icon;

  const hasTopIcons =
    event.extendedProps.reprogramada_cliente ||
    (cfg.mostrar_iconos &&
      (event.extendedProps.telefono ||
        event.extendedProps.observaciones ||
        event.extendedProps.tiene_valoracion ||
        event.extendedProps.num_clientes > 1 ||
        event.extendedProps.cliente_sugerido ||
        showTipoPago));

  const isTiny = availableH > 0 && availableH < (isMobile ? 20 : HORA_H + 4);
  const isLarge =
    isMobile && calendarView === 'timeGridDay' && availableW > 200;
  const spaceForLines = Math.max(0, availableH - HORA_H - 4);
  const maxLines = Math.max(
    infoLines.length > 0 ? 1 : 0,
    Math.floor(spaceForLines / ROW_H)
  );
  const shownLines = infoLines.slice(0, maxLines);

  const abrirWhatsapp = (e: React.MouseEvent, tel: string, mobile: boolean) => {
    e.stopPropagation();
    e.preventDefault();
    e.nativeEvent.stopImmediatePropagation();
    onWhatsappClick();
    const msg = encodeURIComponent(
      t('calendar.whatsappMsg', {
        empresa: event.extendedProps.nombre_empresa,
        fecha: event.extendedProps.raw_fecha_inicio,
        hora: event.extendedProps.raw_hora_inicio,
      })
    );
    const url = mobile
      ? `https://wa.me/${tel}?text=${msg}`
      : `https://web.whatsapp.com/send?phone=${tel}&text=${msg}`;
    window.open(url, '_blank');
  };

  const iconSize = isLarge ? 'w-6 h-6' : 'w-4 h-4';
  const iconPadding = isLarge ? 'p-1' : '';

  const icono1Mobile =
    cfg.mostrar_iconos &&
    isLarge &&
    (event.extendedProps.observaciones ? (
      <span title={event.extendedProps.observaciones}>
        <StickyNote className={`${iconSize} text-yellow-500 opacity-70`} />
      </span>
    ) : event.extendedProps.telefono &&
      est !== 'completado' &&
      est !== 'anulado' ? (
      <button
        onClick={(e) => abrirWhatsapp(e, event.extendedProps.telefono, true)}
        title={t('calendar.sendWhatsapp')}
        className={`hover:opacity-100 transition-opacity opacity-70 touch-manipulation ${iconPadding}`}
      >
        <WhatsappIcon className={iconSize} />
      </button>
    ) : null);

  const icono2Mobile =
    isLarge && (est === 'completado' || est === 'anulado') ? (
      event.extendedProps.tiene_valoracion ? (
        <span
          className={`${
            isLarge ? 'text-xl' : 'text-sm'
          } leading-none opacity-70`}
        >
          ⭐
        </span>
      ) : showTipoPago ? (
        <span
          className={`${
            isLarge ? 'text-xl' : 'text-sm'
          } leading-none opacity-70`}
          title={event.extendedProps.tipo_pago ?? undefined}
        >
          {icon}
        </span>
      ) : null
    ) : null;

  const dotReprogramadaMobile =
    event.extendedProps.reprogramada_cliente &&
    (est === 'confirmado' || est === 'cancelado') && (
      <span
        title={t('calendar.reprogramadaCliente', 'El cliente cambió la fecha/hora')}
        className={`inline-flex ${iconPadding}`}
      >
        <CalendarClock className={`${iconSize} text-orange-500 opacity-70`} />
      </span>
    );

  const iconosMobile = (icono1Mobile || icono2Mobile || dotReprogramadaMobile) && (
    <div className="absolute top-1 right-1 flex flex-col items-end gap-1 z-10">
      {icono1Mobile}
      {icono2Mobile}
      {dotReprogramadaMobile}
    </div>
  );

  const iconosDesktop = !isTiny && hasTopIcons && (
    <div className="flex flex-row items-center gap-1.5 flex-shrink-0 opacity-70">
      {event.extendedProps.reprogramada_cliente &&
        (est === 'confirmado' || est === 'cancelado') && (
        <span title={t('calendar.reprogramadaCliente', 'El cliente cambió la fecha/hora')}>
          <CalendarClock className="w-4 h-4 text-orange-500" />
        </span>
      )}
      {event.extendedProps.observaciones && (
        <span title={event.extendedProps.observaciones}>
          <StickyNote className="w-4 h-4 text-yellow-500" />
        </span>
      )}
      {event.extendedProps.tiene_valoracion && (
        <span className="text-sm leading-none">⭐</span>
      )}
      {event.extendedProps.num_clientes > 1 && (
        <span
          className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-blue-500 dark:text-blue-300 leading-none"
          title={`${event.extendedProps.num_clientes} personas`}
        >
          <Users className="w-4 h-4 text-blue-400" />
          {event.extendedProps.num_clientes}
        </span>
      )}
      {event.extendedProps.cliente_sugerido && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            e.nativeEvent.stopImmediatePropagation();
            agregarCliente(
              idEmpresa,
              event.extendedProps.email ||
                event.extendedProps.email_contacto ||
                '',
              event,
              onReload
            );
          }}
          className="hover:opacity-100 transition-opacity"
        >
          <UserPlus className="w-4 h-4 text-green-500" />
        </button>
      )}
      {cfg.mostrar_iconos && event.extendedProps.telefono && (
        <button
          onClick={(e) => abrirWhatsapp(e, event.extendedProps.telefono, false)}
          title={t('calendar.sendWhatsapp')}
          className="hover:opacity-100 transition-opacity"
        >
          <WhatsappIcon className="w-4 h-4" />
        </button>
      )}
      {showTipoPago && (
        <span
          className="text-sm leading-none"
          title={event.extendedProps.tipo_pago ?? undefined}
        >
          {icon}
        </span>
      )}
    </div>
  );

  return (
    <div
      ref={containerRef}
      className={`w-full h-full rounded-md border-l-[3px] ${borderColor} ${bgColor} px-1.5 py-1 flex flex-col gap-0.5 cursor-pointer transition-all duration-150 hover:brightness-95 dark:hover:brightness-110 overflow-hidden relative`}
    >
      {bandas.map((b) => {
        // Alto real de la franja y zona que ocupa la cabecera de la cita: sin
        // sitio, la etiqueta se monta encima del texto de al lado.
        const altoPx = (b.alto / 100) * availableH;
        const topPx = (b.top / 100) * availableH;
        const cabeceraPx = HORA_H + shownLines.length * ROW_H + 6;
        const libre = topPx >= cabeceraPx;
        const verHora = !isTiny && libre && altoPx >= ROW_H + 2;
        const verNombre = verHora && altoPx >= ROW_H * 2 + 4;

        return (
        <div
          key={b.key}
          className={`absolute left-0 right-0 pointer-events-none overflow-hidden ${
            b.tenir
              ? 'bg-white/65 dark:bg-white/[0.05] border-y border-dashed border-gray-400/25 dark:border-white/10'
              : ''
          }`}
          style={{ top: `${b.top}%`, height: `${b.alto}%` }}
        >
          {verHora && (
            <div className="px-1.5 pt-0.5">
              <span
                className={`block ${timeText} font-medium ${textMuted} leading-none opacity-80 truncate`}
              >
                {b.hora}
              </span>
              {verNombre && b.nombre && (
                <span
                  className={`block mt-0.5 ${infoText} text-gray-500 dark:text-gray-400 leading-none truncate`}
                >
                  {b.nombre}
                </span>
              )}
            </div>
          )}
        </div>
        );
      })}

      {isMobile && iconosMobile}

      <div className="relative flex items-center justify-between gap-1 flex-shrink-0 min-w-0">
        <span
          className={`${timeText} font-medium ${textMuted} leading-none truncate`}
        >
          {isTiny ? hi : `${hi} – ${hf}`}
        </span>
        {!isMobile && iconosDesktop}
      </div>

      {!isTiny &&
        shownLines.map((line) => {
          const lineContent = (
            <div
              className={`relative flex items-center gap-0.5 leading-none flex-shrink-0 ${infoText} min-w-0 ${
                line.key === 'fecha-anterior'
                  ? 'text-gray-400 dark:text-gray-500 line-through cursor-help'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {line.icon}
              <span className="truncate">{line.text}</span>
            </div>
          );

          if (line.key === 'fecha-anterior' && historialReprogramaciones.length > 0) {
            return (
              <ReprogramacionTooltip
                key={line.key}
                historial={historialReprogramaciones}
              >
                {lineContent}
              </ReprogramacionTooltip>
            );
          }

          return (
            <span key={line.key} className="contents">
              {lineContent}
            </span>
          );
        })}
    </div>
  );
}